import type { ObjectTag } from '@atlas/types'

/** Anything carrying resolved classification tags — a file, a message, any classified object. */
export interface Tagged {
	tags: ObjectTag[]
}

/**
 * The subset of `items` that applies under a narrowing: on every named axis, the items
 * tagged for one of its values, plus the items carrying no tag on that axis at all — a logo
 * with no market and no species is universal, not "unclassified for this one". Items tagged
 * only for another market or another species drop out. AND across axes; an empty narrowing
 * keeps everything.
 *
 * The narrowing carries whatever the page is currently answering for: the server's per-axis
 * applicability ladder for the axes it RESOLVED (a market, then its region, then worldwide),
 * and the reader's own filter selection for the axes they narrowed by hand. Both read the
 * same way here — a value the item is tagged for, or no tag at all.
 *
 * Applied on tags in hand rather than in the read's filter because a classification filter
 * matches a value's DESCENDANTS: it would keep the sibling markets' and sibling species'
 * content and drop everything above — the opposite of applicability. An axis the item cannot
 * carry a tag on (an ATTRIBUTE axis such as `aspect_ratio`) therefore narrows nothing here,
 * which is why an attribute selection has to reach the read that can answer it.
 */
export const narrowToApplicable = <T extends Tagged>(items: T[], narrowing: Record<string, string[]>): T[] => {
	const axes = Object.entries(narrowing).filter(([, path]) => path.length > 0)
	if (axes.length === 0) return items
	return items.filter((item) =>
		axes.every(([axisCode, path]) => {
			const tags = item.tags.filter((t) => t.axisCode === axisCode)
			return tags.length === 0 || tags.some((t) => path.includes(t.valueCode))
		}),
	)
}
